import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tag } from 'primereact/tag';
import { FilterMatchMode } from 'primereact/api';
import { adminApi, UserAdmin, OrganizationAdmin } from '../../api/admin';
import { useDataTableState, usePersistentState } from '../../hooks';

export const UsersTab: React.FC = () => {
  const toast = useRef<Toast>(null);
  const [users, setUsers] = useState<UserAdmin[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserAdmin | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);

  // Persistent filter and sort state
  const { sortField, sortOrder, handleSort, filters } = useDataTableState({
    storageKey: 'admin:users',
    defaultSort: { field: 'email', order: 1 },
    defaultFilters: {
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      email: { value: null, matchMode: FilterMatchMode.CONTAINS },
      full_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    },
  });

  // Persistent global search
  const [globalFilterValue, setGlobalFilterValue] = usePersistentState('filters:admin:users:globalSearch', '');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [usersResponse, orgsResponse] = await Promise.all([
        adminApi.listUsers(1, 100),
        adminApi.listOrganizations(1, 100),
      ]);
      setUsers(usersResponse.items);
      setOrganizations(orgsResponse.items);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to load data',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setFullName('');
    setSelectedOrgId(null);
    setIsActive(true);
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (user: UserAdmin) => {
    setEditingUser(user);
    setEmail(user.email);
    setFullName(user.full_name || '');
    setIsActive(user.is_active);
    // Find org ID by name
    const org = organizations.find((o) => o.name === user.organization_name);
    setSelectedOrgId(org?.id || null);
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!email.trim()) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Email is required',
        life: 3000,
      });
      return;
    }

    try {
      setIsSaving(true);
      if (editingUser) {
        const updated = await adminApi.updateUser(editingUser.id, {
          email,
          full_name: fullName || undefined,
          is_active: isActive,
          org_id: selectedOrgId || '',
        });
        setUsers(users.map((u) => (u.id === updated.id ? updated : u)));
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'User updated successfully',
          life: 3000,
        });
      } else {
        const created = await adminApi.createUser({
          email,
          full_name: fullName || undefined,
          org_id: selectedOrgId || undefined,
        });
        setUsers([created, ...users]);
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'User created successfully',
          life: 3000,
        });
      }
      setShowDialog(false);
      resetForm();
      setEditingUser(null);
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.response?.data?.detail || 'Failed to save user',
        life: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (user: UserAdmin) => {
    confirmDialog({
      message: `Are you sure you want to delete "${user.email}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await adminApi.deleteUser(user.id);
          setUsers(users.filter((u) => u.id !== user.id));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'User deleted successfully',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to delete user',
            life: 5000,
          });
        }
      },
    });
  };

  const handleReset2FA = (user: UserAdmin) => {
    confirmDialog({
      message: `Are you sure you want to reset 2FA for "${user.email}"? This will disable their two-factor authentication and give them a new 7-day grace period to set it up again.`,
      header: 'Reset Two-Factor Authentication',
      icon: 'ms-out ms-out-warning icon-warning',
      acceptClassName: 'p-button-warning',
      accept: async () => {
        try {
          const updated = await adminApi.reset2FA(user.id);
          setUsers(users.map((u) => (u.id === updated.id ? updated : u)));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: '2FA has been reset for this user',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to reset 2FA',
            life: 5000,
          });
        }
      },
    });
  };

  const handleExtendGracePeriod = (user: UserAdmin) => {
    confirmDialog({
      message: `Extend the 2FA grace period for "${user.email}" by 7 days? This gives them more time to set up two-factor authentication.`,
      header: 'Extend Grace Period',
      icon: 'ms-out ms-out-clock icon-info',
      acceptClassName: 'p-button-info',
      accept: async () => {
        try {
          const updated = await adminApi.extendGracePeriod(user.id);
          setUsers(users.map((u) => (u.id === updated.id ? updated : u)));
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Grace period has been extended by 7 days',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to extend grace period',
            life: 5000,
          });
        }
      },
    });
  };

  const handleSendPasswordSetup = (user: UserAdmin) => {
    confirmDialog({
      message: `Send a password setup email to "${user.email}"? This will generate a new password reset link.`,
      header: 'Send Password Setup Email',
      icon: 'ms-out ms-out-mail icon-info',
      acceptClassName: 'p-button-info',
      accept: async () => {
        try {
          await adminApi.resendWelcomeEmail(user.id);
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Password setup email sent',
            life: 3000,
          });
        } catch (error: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Error',
            detail: error.response?.data?.detail || 'Failed to send email',
            life: 5000,
          });
        }
      },
    });
  };

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGlobalFilterValue(e.target.value);
  };

  const actionBodyTemplate = (rowData: UserAdmin) => {
    return (
      <div className="row-actions">
        <Button
          icon="ms-out ms-out-edit"
          link
          tooltip="Edit"
          onClick={() => openEditDialog(rowData)}
        />
        <Button
          icon="ms-out ms-out-mail"
          link
          tooltip="Send Password Setup Email"
          onClick={() => handleSendPasswordSetup(rowData)}
        />
        {rowData.totp_enabled ? (
          <Button
            icon="ms-out ms-out-lock-reset"
            link
            tooltip="Reset 2FA"
            onClick={() => handleReset2FA(rowData)}
          />
        ) : (
          <Button
            icon="ms-out ms-out-snooze"
            link
            tooltip="Extend Grace Period"
            onClick={() => handleExtendGracePeriod(rowData)}
          />
        )}
        <Button
          icon="ms-out ms-out-delete"
          link
          tooltip="Delete"
          onClick={() => handleDelete(rowData)}
        />
      </div>
    );
  };

  const dateBodyTemplate = (rowData: UserAdmin) => {
    return new Date(rowData.created_at).toLocaleDateString();
  };

  const statusBodyTemplate = (rowData: UserAdmin) => {
    return (
      <Tag
        value={rowData.is_active ? 'Active' : 'Inactive'}
        severity={rowData.is_active ? 'success' : 'danger'}
      />
    );
  };

  const twoFactorBodyTemplate = (rowData: UserAdmin) => {
    if (rowData.totp_enabled) {
      return <Tag value="Enabled" severity="success" />;
    }
    // Check if grace period has expired
    if (rowData.totp_grace_period_ends_at) {
      const gracePeriodEnd = new Date(rowData.totp_grace_period_ends_at);
      if (new Date() > gracePeriodEnd) {
        return <Tag value="Expired" severity="danger" />;
      }
      return <Tag value="Pending" severity="warning" />;
    }
    return <Tag value="Not Set" severity="secondary" />;
  };


  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="controls-panel">
        <Button
          label="Add User"
          icon="ms-out ms-out-add"
          onClick={openCreateDialog}
        />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder="Search..."
        />
      </div>

      <DataTable
        value={users}
        dataKey="id"
        loading={isLoading}
        size="small"
        stripedRows
        emptyMessage="No users found"

        scrollable
        scrollHeight="calc(100vh - 200px)"

        paginator
        rows={25}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout:
            'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of{' '}
              <b>{options.totalRecords}</b> users
            </span>
          ),
        }}

        filters={filters}
        globalFilterFields={['email', 'full_name', 'organization_name']}
        globalFilter={globalFilterValue}
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={handleSort}
      >
        <Column field="email" header="Email" sortable filter style={{ width: '22%' }} />
        <Column field="full_name" header="Full Name" sortable filter style={{ width: '15%' }} />
        <Column
          field="is_active"
          header="Status"
          body={statusBodyTemplate}
          sortable
          style={{ width: '8%' }}
        />
        <Column
          field="totp_enabled"
          header="2FA"
          body={twoFactorBodyTemplate}
          sortable
          style={{ width: '8%' }}
        />
        <Column field="organization_name" header="Organization" sortable filter style={{ width: '17%' }} />
        <Column
          field="created_at"
          header="Created"
          body={dateBodyTemplate}
          sortable
          style={{ width: '12%' }}
        />
        <Column body={actionBodyTemplate} />
      </DataTable>

      <Dialog
        header={editingUser ? 'Edit User' : 'Add User'}
        visible={showDialog}
        className="user-dialog"
        onHide={() => {
          setShowDialog(false);
          resetForm();
          setEditingUser(null);
        }}

        footer={
        <>
          <Button
            label="Cancel"
            icon="ms-out ms-out-close"
            onClick={() => setShowDialog(false)}
            severity="secondary"
            outlined
          />
          <Button
            label={editingUser ? 'Update' : 'Create'}
            icon="ms-out ms-out-check"
            onClick={handleSave}
            severity="success"
            loading={isSaving}
          />
        </>
        }
      >
        <div className="form-grid">
          <div className="form-field full-width">
            <label htmlFor="email">Email *</label>
            <InputText
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter email"
              className="full-width-input"
              autoFocus
            />
          </div>

          <div className="form-field full-width">
            <label htmlFor="fullName">Full Name</label>
            <InputText
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter full name"
              className="full-width-input"
            />
          </div>

          <div className="form-field full-width">
            <label htmlFor="organization">Organization</label>
            <Dropdown
              id="organization"
              value={selectedOrgId}
              options={organizations}
              onChange={(e) => setSelectedOrgId(e.value)}
              optionLabel="name"
              optionValue="id"
              placeholder="Select organization"
              className="full-width-input"
              showClear
            />
          </div>

          {editingUser && (
            <div className="form-field full-width">
              <label htmlFor="isActive">Active</label>
              <InputSwitch
                id="isActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.value)}
              />
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default UsersTab;
